import { TaxCalculationData, TaxedLineItem } from '@commenda/api';
import { TransactionSourcePlatformText } from '@commenda/api/sales-tax/constants';
import { Badge, DataTable } from '@commenda/kakashi';
import { CurrencyHelper } from '@commenda/utils/currency';
import { Currency } from '@prisma/client';
import { useRouter } from 'next/router';
import { getStateName } from '../utils';
import { CalculationEntityTypeToText } from './types';

interface CalculationsTableProps {
  data: TaxCalculationData[];
  pageSize?: number;
  nextCursor?: string | null;
  currentCursor?: string | null;
  onPageChange?: (cursor: string | null) => void;
  corporationId: number;
  // Optional filter configuration to render DataTable toolbar filters
  // Match Kakashi DataTable FilterConfig shape without importing type here
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filterConfig?: any;
  searchTerm?: string;
  handleSearch?: (term: string) => void;
}

const CalculationsTable = ({
  data,
  pageSize = 10,
  nextCursor,
  currentCursor,
  onPageChange,
  corporationId,
  filterConfig,
  searchTerm = '',
  handleSearch,
}: CalculationsTableProps) => {
  const router = useRouter();

  const defaultVisibleColumns = [
    'CalculationDate',
    'CreatedAt',
    'subtotal',
    'taxableAmount',
    'tax',
    'total',
    'location',
    'postalCode',
    'address',
    'SourcePlatform',
    'SourcePlatformEntityType',
    'SourcePlatformEntityID',
  ];

  const SALES_TAX_CALCULATIONS_TABLE_COLUMN_GROUP_STORAGE_KEY = `sales-tax-calculations-table-column-group-corporation-${corporationId}`;

  const getInitialVisibleColumns = () => {
    const storageKey = SALES_TAX_CALCULATIONS_TABLE_COLUMN_GROUP_STORAGE_KEY;
    const storedColumns = localStorage.getItem(storageKey);
    return storedColumns ? JSON.parse(storedColumns) : defaultVisibleColumns;
  };

  const handleNextPage = () => {
    if (nextCursor && onPageChange) {
      onPageChange(nextCursor);
    }
  };

  const handlePreviousPage = () => {
    if (onPageChange) {
      onPageChange(currentCursor);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const formatAddress = (address: any) => {
    const stateName = getStateName(address.state);

    const parts = [
      address.address_line_1,
      address.address_line_2,
      address.address_line_3,
      address.city,
      stateName,
      address.postal_code,
      address.country,
    ].filter(Boolean);
    return parts.join(', ');
  };

  const calculateTotals = (lineItems: TaxedLineItem[]) => {
    return lineItems.reduce(
      (acc, item) => ({
        subtotal: acc.subtotal + item.amount,
        taxableAmount: acc.taxableAmount + item.taxable_amount,
        totalTax: acc.totalTax + item.total_tax_due,
      }),
      { subtotal: 0, taxableAmount: 0, totalTax: 0 }
    );
  };

  const columnGroups = [
    {
      id: 'dates',
      columns: [
        {
          accessorKey: 'CalculationDate',
          header: 'Calculation Date',
          title: 'Calculation Date',
          cell: ({ row }) => {
            const value = row.getValue('CalculationDate');
            return value ? (
              <p className="txt-small-regular text-text-primary">{value}</p>
            ) : (
              <Badge variant="error">Missing</Badge>
            );
          },
        },
        {
          accessorKey: 'CreatedAt',
          header: 'Created At',
          title: 'Created At',
          cell: ({ row }) => {
            const value = row.getValue('CreatedAt');
            return value ? (
              <p className="txt-small-regular text-text-tertiary">{value}</p>
            ) : (
              <Badge variant="error">Missing</Badge>
            );
          },
        },
      ],
    },
    {
      id: 'amounts',
      columns: [
        {
          accessorKey: 'subtotal',
          header: 'Subtotal',
          title: 'Subtotal',
          headerClassName: 'w-full justify-end',
          cell: ({ row }) => {
            const lineItems = row.original.Output?.line_items;
            if (!lineItems?.length) {
              return <Badge variant="error">Missing</Badge>;
            }
            const totals = calculateTotals(lineItems);
            return (
              <p className="flex justify-end txt-small-regular text-text-primary">
                {CurrencyHelper.formatCurrency(
                  row.original.Output?.transaction_currency as Currency,
                  totals.subtotal,
                  { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                )}
              </p>
            );
          },
        },
        {
          accessorKey: 'taxableAmount',
          header: 'Taxable Amount',
          title: 'Taxable Amount',
          headerClassName: 'w-full justify-end',
          cell: ({ row }) => {
            const lineItems = row.original.Output?.line_items;
            if (!lineItems?.length) {
              return <Badge variant="error">Missing</Badge>;
            }
            const totals = calculateTotals(lineItems);
            return (
              <p className="flex justify-end txt-small-regular text-text-primary">
                {CurrencyHelper.formatCurrency(
                  row.original.Output?.transaction_currency as Currency,
                  totals.taxableAmount,
                  { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                )}
              </p>
            );
          },
        },
        {
          accessorKey: 'tax',
          header: 'Tax',
          title: 'Tax',
          headerClassName: 'w-full justify-end',
          cell: ({ row }) => {
            const lineItems = row.original.Output?.line_items;
            if (!lineItems?.length) {
              return <Badge variant="error">Missing</Badge>;
            }
            const totals = calculateTotals(lineItems);
            return (
              <p className="flex justify-end txt-small-regular text-text-primary">
                {CurrencyHelper.formatCurrency(
                  row.original.Output?.transaction_currency as Currency,
                  totals.totalTax,
                  { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                )}
              </p>
            );
          },
        },
        {
          accessorKey: 'total',
          header: 'Total',
          title: 'Total',
          headerClassName: 'w-full justify-end',
          cell: ({ row }) => {
            const lineItems = row.original.Output?.line_items;
            if (!lineItems?.length) {
              return <Badge variant="error">Missing</Badge>;
            }
            const totals = calculateTotals(lineItems);
            const total = totals.subtotal + totals.totalTax;
            return (
              <p className="flex justify-end txt-small-medium text-text-primary font-semibold">
                {CurrencyHelper.formatCurrency(
                  row.original.Output?.transaction_currency as Currency,
                  total,
                  { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                )}
              </p>
            );
          },
        },
      ],
    },
    {
      id: 'address',
      columns: [
        {
          accessorKey: 'location',
          header: 'State/Country',
          title: 'State/Country',
          cell: ({ row }) => {
            const shipTo = row.original.Output?.addresses?.ship_to;
            return shipTo?.state && shipTo?.country ? (
              <p className="txt-small-regular text-text-primary">
                {`${shipTo.state}, ${shipTo.country}`}
              </p>
            ) : (
              <Badge variant="error">Missing</Badge>
            );
          },
        },
        {
          accessorKey: 'postalCode',
          header: 'Postal Code',
          title: 'Postal Code',
          cell: ({ row }) => {
            const postalCode =
              row.original.Output?.addresses?.ship_to?.postal_code;
            return postalCode ? (
              <p className="txt-small-regular text-text-primary">
                {postalCode}
              </p>
            ) : (
              <Badge variant="error">Missing</Badge>
            );
          },
        },
        {
          accessorKey: 'address',
          header: 'Address',
          title: 'Address',
          cell: ({ row }) => {
            const address = row.original.Output?.addresses?.ship_to;
            return address ? (
              <p className="txt-small-regular text-text-primary">
                {formatAddress(address)}
              </p>
            ) : (
              <Badge variant="error">Missing</Badge>
            );
          },
        },
      ],
    },
    {
      id: 'source',
      columns: [
        {
          accessorKey: 'SourcePlatform',
          header: 'Source Platform',
          title: 'Source Platform',
          cell: ({ row }) => {
            const value = row.getValue('SourcePlatform');
            return value ? (
              <p className="txt-small-regular text-text-primary">
                {TransactionSourcePlatformText[value]}
              </p>
            ) : (
              <Badge variant="error">Missing</Badge>
            );
          },
        },
        {
          accessorKey: 'SourcePlatformEntityType',
          header: 'Entity Type',
          title: 'Entity Type',
          cell: ({ row }) => {
            const value = row.getValue('SourcePlatformEntityType');
            return value ? (
              <p className="txt-small-regular text-text-primary">
                {CalculationEntityTypeToText[value]}
              </p>
            ) : (
              <Badge variant="error">Missing</Badge>
            );
          },
        },
        {
          accessorKey: 'SourcePlatformEntityID',
          header: 'Entity ID',
          title: 'Entity ID',
          cell: ({ row }) => {
            const value = row.getValue('SourcePlatformEntityID');
            return value ? (
              <p className="txt-small-regular text-text-primary">{value}</p>
            ) : (
              <Badge variant="error">Missing</Badge>
            );
          },
        },
      ],
    },
  ];

  const getEmptyStateConfig = () => ({
    title: 'No calculations found',
    description: (
      <>
        There are no calculations to display at this time. Try refining <br />
        your search term, adjusting the filters, or reset both to see all
        calculations.
      </>
    ),
  });

  const handleCalculationRowClick = (row: TaxCalculationData) => {
    router.push(
      `/sales-tax/calculation/${row.ID}?corporationId=${corporationId}`
    );
  };

  return (
    <DataTable
      onRowClick={(row) => {
        handleCalculationRowClick(row);
      }}
      columnGroups={columnGroups}
      data={data}
      paginationConfig={{
        isEnabled: true,
        pageSize,
        type: 'CURSOR',
        hasNextPage: !!nextCursor,
        onNextPage: handleNextPage,
        onPreviousPage: handlePreviousPage,
        hasPreviousPage: !!currentCursor,
      }}
      searchConfig={{
        isEnabled: true,
        placeholder: 'Search by ID, Entity ID',
        searchValue: searchTerm,
        columnsToEnableSearchFor: ['ID', 'SourcePlatformEntityID'],
        onSearch: (value: string) => {
          handleSearch?.(value);
        },
      }}
      tableViewConfig={{
        isEnabled: true,
        defaultView: 'expanded',
      }}
      columnSelectorConfig={{
        isEnabled: true,
        placeholder: 'Customise columns',
        searchText: 'Search columns',
        notFoundText: 'No columns found',
        selectAllText: 'Show all columns',
        defaultVisibleColumns: getInitialVisibleColumns(),
        onColumnVisibilityChange: (visibleColumns) => {
          localStorage.setItem(
            SALES_TAX_CALCULATIONS_TABLE_COLUMN_GROUP_STORAGE_KEY,
            JSON.stringify(visibleColumns)
          );
        },
      }}
      filterConfig={filterConfig}
      emptyStateConfig={getEmptyStateConfig()}
    />
  );
};

export default CalculationsTable;
